import { useRef, useState } from "react";
import { CreditCard, Plus } from "lucide-react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useAppointment } from "@/features/appointments/hooks/appointmentQueries";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { useClient } from "@/features/clients/hooks/clientQueries";
import { usePaymentMethods } from "@/features/payment-methods/hooks/paymentMethodQueries";
import type { PaymentMethod } from "@/features/payment-methods/types/paymentMethodTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { Checkbox } from "@/shared/components/shadcn/Checkbox";
import { AmountInput } from "@/shared/components/ui/AmountInput";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useWorkspaceOperation } from "@/shared/hooks/useWorkspaceOperation";
import { usePaymentMutations, usePaymentSummary } from "../hooks/paymentQueries";
import {
  formatPaymentMoney, parsePaymentAmount, paymentAmountInput, paymentCurrencyDigits,
  type CreatePaymentRequest, type PaymentSummary,
} from "../types/paymentTypes";

function ChargeForm({ appointmentId, clientId, businessId, userId, revision, summary }: {
  appointmentId: string; clientId: string; businessId: string; userId: string; revision: number; summary: PaymentSummary;
}) {
  const cards = usePaymentMethods(businessId, clientId, userId);
  const client = useClient(clientId);
  const { create } = usePaymentMutations(appointmentId);
  const capture = useWorkspaceOperation();
  const { goToAppointmentPayment } = useAppNavigation();
  const [amount, setAmount] = useState(() => paymentAmountInput(summary.balanceMinor ?? 0, summary.currency));
  const [selected, setSelected] = useState<PaymentMethod | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const attempt = useRef<CreatePaymentRequest | null>(null);
  const submitting = useRef(false);
  const amountMinor = parsePaymentAmount(amount, summary.currency);
  const amountError = amountMinor === null ? `Enter a positive amount${summary.currency === "JPY" ? " with no decimal places" : " with up to 2 decimal places"}.` :
    amountMinor > (summary.balanceMinor ?? 0) ? "The amount cannot exceed the uncollected balance." : undefined;
  const busy = create.isPending;
  const locked = busy || !!attempt.current;
  const now = new Date();
  const cardsMatchEnvironment = !cards.data || cards.data.environment === summary.environment;
  const customerName = client.data ? [client.data.firstName, client.data.lastName].filter(Boolean).join(" ") : "this customer";
  function submit(card?: PaymentMethod) {
    if (submitting.current || amountError || !amountMinor || (card && !acknowledged)) return;
    attempt.current ??= {
      amountMinor, expectedRevision: revision, expectedCurrency: summary.currency, idempotencyKey: crypto.randomUUID(),
      method: card ? "saved_card" : "new_card",
      ...(card ? { paymentMethodId: card.id, acknowledged: true } : {}),
    };
    submitting.current = true;
    const isCurrent = capture();
    create.mutate(attempt.current, {
      onSuccess: payment => { if (isCurrent()) goToAppointmentPayment(appointmentId, payment.id); },
      onSettled: () => { submitting.current = false; },
    });
  }
  return <div className="space-y-4">
    {client.error ? <ErrorState message={client.error.message} onRetry={() => void client.refetch()} /> :
      <p className="font-medium">{customerName}</p>}
    <p className="text-sm text-muted-foreground">
      Collected {formatPaymentMoney(summary.collectedMinor, summary.currency)} of {formatPaymentMoney(summary.totalMinor ?? 0, summary.currency)}.
      {" "}Uncollected {formatPaymentMoney(summary.balanceMinor ?? 0, summary.currency)}.
    </p>
    <p className="text-xs text-muted-foreground">{summary.environment === "test" ? "Test mode - use test cards only." : "Live mode - real money will be collected."}</p>
    <fieldset disabled={locked} className="min-w-0">
      <AmountInput label={`Amount to charge (${summary.currency})`} value={amount} onValueChange={setAmount} error={amountError} />
    </fieldset>
    <fieldset disabled={locked || !!amountError || !!client.error || client.isPending} className="min-w-0 space-y-4 disabled:opacity-60">
      <legend className="sr-only">Payment options</legend>
      <InfoRow title="New charge" subtitle="Let the customer pay on this device or with a secure link."
        icon={<Plus aria-hidden="true" />} onClick={() => submit()} className="rounded-[20px]" />
      {cards.isPending ? <RowSkeleton /> : cards.error || !cardsMatchEnvironment ? null :
        cards.data.cards.length === 0 ? <p className="text-sm text-muted-foreground">No saved cards.</p> :
          cards.data.cards.map(card => {
            const expired = card.expYear < now.getFullYear() || (card.expYear === now.getFullYear() && card.expMonth < now.getMonth() + 1);
            return <fieldset key={card.id} disabled={expired} className="min-w-0 disabled:opacity-60">
              <InfoRow title={`${card.brand} ending in ${card.last4}`}
                subtitle={`${expired ? "Expired" : "Expires"} ${String(card.expMonth).padStart(2, "0")}/${card.expYear}`}
                icon={<CreditCard aria-hidden="true" />} className="rounded-[20px]"
                onClick={() => { setAcknowledged(false); setSelected(card); }} />
            </fieldset>;
          })}
    </fieldset>
    {cards.error && <ErrorState message={cards.error.message} onRetry={() => void cards.refetch()} />}
    {!cardsMatchEnvironment && <ErrorState message="Saved cards do not match this payment's Stripe environment. Refresh cards before using one."
      onRetry={() => void cards.refetch()} />}
    {create.error && <div className="space-y-2">
      <ErrorState message={create.error.message} />
      <ErrorState message="The payment request could not be resolved. It may have reached Stripe. Refresh payments or retry this same request; do not start a replacement." />
      <Button disabled={busy} onClick={() => submit(selected ?? undefined)}>Retry this request</Button>
    </div>}
    <ConfirmationDialog open={!!selected} onOpenChange={open => { if (!open && !busy) setSelected(null); }}
      title="Charge saved card?" confirmLabel={`Charge ${formatPaymentMoney(amountMinor ?? 0, summary.currency)}`}
      dismissLabel="Back" pending={busy} confirmDisabled={!acknowledged || !!amountError}
      error={create.error ? "The result is not confirmed. Retry the same request or check appointment payments." : undefined}
      description={<>
        Charge {customerName} {formatPaymentMoney(amountMinor ?? 0, summary.currency)} using {selected?.brand} ending in {selected?.last4}.
        <label className="mt-3 flex items-start gap-2">
          <Checkbox checked={acknowledged} disabled={locked} onCheckedChange={value => setAcknowledged(value === true)} />
          <span>I confirm the customer agreed to this amount and timing for this appointment.</span>
        </label>
      </>}
      onConfirm={() => { if (selected) submit(selected); }} />
  </div>;
}

export function AppointmentChargePage() {
  const { appointmentId = "" } = useParams();
  const { business, businessId } = useCurrentBusiness();
  const { user } = useAuth();
  const appointment = useAppointment(appointmentId);
  const summary = usePaymentSummary(appointmentId);
  const { goToAppointmentPayment } = useAppNavigation();
  const authorized = !!businessId && !!user && business?.ownerId === user.id;
  return <Page>
    <PageHeader title="Charge appointment" />
    <PageContent><div className="mx-auto max-w-2xl space-y-4">
      {!authorized ? <ErrorState message="Only the business owner can charge an appointment." /> :
        appointment.isPending || summary.isPending ? <RowSkeleton /> :
          appointment.error || summary.error ? <ErrorState message={appointment.error?.message ?? summary.error!.message}
            onRetry={() => { void appointment.refetch(); void summary.refetch(); }} /> :
            summary.data?.activePayment ? <>
              <p>This appointment already has an unresolved payment. Resume or cancel it before selecting another amount or card.</p>
              <Button onClick={() => goToAppointmentPayment(appointmentId, summary.data.activePayment!.id)}>Resume payment</Button>
            </> : !summary.data?.canCharge || !appointment.data?.clientId || paymentCurrencyDigits(summary.data.currency) === null ?
              <ErrorState message={summary.data?.unavailableReason ?? "This appointment cannot be charged. Check its customer, price, and currency."} /> :
              <ChargeForm key={`${user.id}:${businessId}:${appointmentId}:${summary.data.currency}:${appointment.data.revision}`} appointmentId={appointmentId}
                clientId={appointment.data.clientId} businessId={businessId} userId={user.id}
                revision={appointment.data.revision} summary={summary.data} />}
    </div></PageContent>
  </Page>;
}
